/* AG-Grid */
import { AgGridReact } from 'ag-grid-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import 'ag-grid-enterprise'
import 'ag-grid-enterprise/styles/ag-grid.css'
import 'ag-grid-enterprise/styles/ag-theme-material.css'
import 'ag-grid-community'
import './ag-grid-styles.css'
import { localeText } from '../../actions/datagrid'
import {
	gridSettings as commonGridSettings,
	sideBar as commonSideBar,
	statusBar as commonStatusBar,
	contextMenu,
	loadViewState,
	saveViewState,
} from '../grid'
import { restoreIconString } from '../icons'

const defaultColDefProps = {
	editable: false,
	sortable: true,
	filter: true,
	resizable: true,
	enableRowGroup: true,
	enablePivot: true,
	enableValue: true,
	filterParams: {
		buttons: ['reset'],
	},
}

/** WIP */
export const AgGridCommonList = (props) => {
	const {
		columns: columnDefinition,
		rowData: data,
		/* currentLocale, */
		gridRef,
		className,
		themeClassName = 'ag-theme-material styles-ag',
		defaultColDef = defaultColDefProps,
		gridSettings = commonGridSettings,
		sideBar = commonSideBar,
		statusBar = commonStatusBar,
		components,
		getRowId,
		getContextMenuItems,
		initialState,
		viewStateKey,
		persistState = true,
		agGridProps = {},
		rowClassRules,
		onStateUpdated,
		onGridReady,
		onFirstDataRendered,
		onFilterChanged,
		onRowDoubleClicked,
		handleSelectionChanged,
		handleFilterChanged,
		handleDblClick,
		handleGridReady,
		handleFirstDataRendered,
		defaultFilterDefinition,
		defaultSortOptions /* ,
    hideFiltersSidebar,
    hideStatusBar = false */,
	} = props

	const { t, i18n } = useTranslation()

	const [api, setApi] = useState(null)

	const columns = useMemo(() => (Array.isArray(columnDefinition) ? columnDefinition : []), [columnDefinition])
	const rowData = useMemo(() => (api ? data : []), [data, api])

	/*   const locale = currentLocale ?? i18n?.resolvedLanguage ?? i18n?.language */

	const resolvedInitialState = useMemo(() => {
		if (persistState && viewStateKey) {
			const savedState = loadViewState(viewStateKey)
			if (savedState && Object.keys(savedState).length > 0) {
				return savedState
			}
		}
		return initialState
	}, [persistState, viewStateKey, initialState])

	const onGridReadyHandler = useCallback(
		(params) => {
			const { api } = params
			setApi(api)
			handleGridReady?.(params)
			onGridReady?.(params)
		},
		[handleGridReady, onGridReady],
	)

	// Without this the columnDefs with {hidden: true} are always initially hidden regardless of the saved state
	useEffect(() => {
		if (api && columns.length > 0) {
			api.setState(resolvedInitialState)
		}
	}, [api, columns, resolvedInitialState])

	const onFirstDataRenderedHandler = useCallback(
		(params) => {
			const { api } = params
			if (defaultFilterDefinition) {
				api.setFilterModel(defaultFilterDefinition)
			}
			if (defaultSortOptions) {
				api.setSortModel(defaultSortOptions)
			}
			handleFirstDataRendered?.(params)
			onFirstDataRendered?.(params)
		},
		[defaultFilterDefinition, defaultSortOptions, handleFirstDataRendered, onFirstDataRendered],
	)

	const onFilterChangedHandler = useCallback(
		(params) => {
			handleFilterChanged?.(params)
			onFilterChanged?.(params)
		},
		[handleFilterChanged, onFilterChanged],
	)

	const onSelectionChangedHandler = useCallback(
		(params) => {
			handleSelectionChanged?.(params)
		},
		[handleSelectionChanged],
	)

	const onRowDoubleClickedHandler = useCallback(
		(params) => {
			handleDblClick?.(params)
			onRowDoubleClicked?.(params)
		},
		[handleDblClick, onRowDoubleClicked],
	)

	const getContextMenuItemsHandler = useCallback(
		(params) => {
			const resetItem = {
				name: t('DefaultGridValues', 'Palauta taulukon oletusasetukset'),
				action: () => {
					params.api.resetColumnState()
					params.api.resetColumnGroupState()
				},
				icon: restoreIconString,
			}

			const baseItems = getContextMenuItems ? getContextMenuItems(params) : contextMenu(t, params, null, null)

			return [resetItem, 'separator', ...baseItems]
		},
		[getContextMenuItems, t],
	)

	const agGridPropsGetMainMenuItems = agGridProps.getMainMenuItems
	const getMainMenuItemsHandler = useCallback(
		(params) => {
			const baseItems = agGridPropsGetMainMenuItems ? agGridPropsGetMainMenuItems(params) : params.defaultItems
			return [
				...baseItems,
				'separator',
				{
					name: t('DefaultGridValues', 'Palauta taulukon oletusasetukset'),
					action: () => {
						params.api.resetColumnState()
						params.api.resetColumnGroupState()
					},
					icon: restoreIconString,
				},
			]
		},
		[agGridPropsGetMainMenuItems, t],
	)

	const onStateUpdatedHandler = useCallback(
		(params) => {
			if (persistState && viewStateKey && params?.state) {
				saveViewState(params.state, viewStateKey)
			}
			onStateUpdated?.(params)
		},
		[persistState, viewStateKey, onStateUpdated],
	)

	const onCellClicked = (params) => {
		if (params.colDef.checkboxSelection) {
			params.node.setSelected(!params.node.isSelected())
		}
	}

	const containerClassName = useMemo(
		() => [themeClassName, 'styles-ag', 'hr-easy-common-list', className].filter(Boolean).join(' '),
		[themeClassName, className],
	)

	return (
		<div className={containerClassName} data-testid="ag-grid-common-list-container">
			<AgGridReact
				{...gridSettings}
				{...agGridProps}
				rowClassRules={rowClassRules}
				ref={gridRef}
				components={components}
				columnDefs={columns}
				rowData={rowData}
				defaultColDef={defaultColDef}
				localeText={localeText(i18n)}
				sideBar={sideBar}
				statusBar={statusBar}
				getRowId={getRowId}
				getContextMenuItems={getContextMenuItemsHandler}
				getMainMenuItems={getMainMenuItemsHandler}
				initialState={resolvedInitialState}
				onGridReady={onGridReadyHandler}
				onFirstDataRendered={onFirstDataRenderedHandler}
				onFilterChanged={onFilterChangedHandler}
				onSelectionChanged={onSelectionChangedHandler}
				onRowDoubleClicked={onRowDoubleClickedHandler}
				onStateUpdated={onStateUpdatedHandler}
				onCellClicked={onCellClicked}
			/>
		</div>
	)
}

export default AgGridCommonList
